import { alpha, Box, CircularProgress, Collapse, Typography, useTheme } from '@mui/material';
import { RiCheckboxCircleFill, RiErrorWarningFill } from '@remixicon/react';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { StatusEntry, StatusEntryVariant, useAiChatStore } from '../store/aiChat.store';

const MIN_DISPLAY_TIME = 800; // Minimum time a toast stays visible (ms)
const DISMISS_DELAY = 1200; // Time after completion before dismissing (ms)

// Memoized status toast item
const StatusToast = memo(
  function StatusToast({
    groupId,
    entry,
    onDismiss,
  }: {
    groupId: string;
    entry: StatusEntry;
    onDismiss: (groupId: string) => void;
  }) {
    const theme = useTheme();
    const dismissTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isCompleted = entry.status === 'completed';
    const variant: StatusEntryVariant = entry.variant ?? 'info';

    // Get colors based on variant (completed items always use success colors)
    const colors = useMemo(() => {
      // When completed, transition to success styling
      if (isCompleted) {
        return {
          background: alpha(theme.palette.success.main, 0.15),
          text: theme.palette.success.dark,
          Icon: RiCheckboxCircleFill,
        };
      }

      switch (variant) {
        case 'success':
          return {
            background: alpha(theme.palette.success.main, 0.15),
            text: theme.palette.success.dark,
            Icon: RiCheckboxCircleFill,
          };
        case 'warning':
          return {
            background: alpha(theme.palette.warning.main, 0.15),
            text: theme.palette.warning.dark,
            Icon: RiErrorWarningFill,
          };
        case 'error':
          return {
            background: alpha(theme.palette.error.main, 0.15),
            text: theme.palette.error.dark,
            Icon: RiErrorWarningFill,
          };
        case 'info':
        default:
          return {
            background: alpha(theme.palette.info.main, 0.15),
            text: theme.palette.info.dark,
            Icon: RiCheckboxCircleFill,
          };
      }
    }, [variant, theme, isCompleted]);

    // Auto-dismiss completed entries after ensuring minimum display time
    useEffect(() => {
      if (isCompleted && !dismissTimeoutRef.current) {
        const elapsed = Date.now() - entry.createdAt;
        const remainingMinTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);
        const totalDelay = remainingMinTime + DISMISS_DELAY;

        dismissTimeoutRef.current = setTimeout(() => {
          onDismiss(groupId);
        }, totalDelay);
      }

      return () => {
        if (dismissTimeoutRef.current) {
          clearTimeout(dismissTimeoutRef.current);
          dismissTimeoutRef.current = null;
        }
      };
    }, [isCompleted, groupId, entry.createdAt, onDismiss]);

    const displayMessage = entry.label ? `${entry.label}: ${entry.message}` : entry.message;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          mb: 1,
          backgroundColor: colors.background,
          borderRadius: '8px',
          opacity: isCompleted ? 0.7 : 1,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {isCompleted || variant === 'error' || variant === 'warning' ? (
          <colors.Icon size={18} color={colors.text} />
        ) : (
          <CircularProgress size={18} sx={{ color: colors.text }} />
        )}
        <Typography variant="body2" sx={{ fontWeight: 500, color: colors.text, transition: 'color 0.3s ease-in-out' }}>
          {displayMessage}
        </Typography>
      </Box>
    );
  },
  // Custom comparison - only re-render if entry content changes
  (prevProps, nextProps) =>
    prevProps.groupId === nextProps.groupId &&
    prevProps.entry.status === nextProps.entry.status &&
    prevProps.entry.message === nextProps.entry.message &&
    prevProps.entry.label === nextProps.entry.label &&
    prevProps.entry.variant === nextProps.entry.variant
);

export default function AiChatToolCalls() {
  const statusEntries = useAiChatStore((state) => state.statusEntries);
  const removeStatus = useAiChatStore((state) => state.removeStatus);

  // Stable callback reference
  const handleDismiss = useCallback(
    (groupId: string) => {
      removeStatus(groupId);
    },
    [removeStatus]
  );

  const entries = Object.entries(statusEntries);

  return (
    <TransitionGroup>
      {entries.map(([groupId, entry]) => (
        <Collapse key={groupId}>
          <StatusToast groupId={groupId} entry={entry} onDismiss={handleDismiss} />
        </Collapse>
      ))}
    </TransitionGroup>
  );
}
